import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { services, company, fairness } from "@/data/site";
import { FadeUp, Lines } from "@/components/motion/Reveal";
import { CtaBand } from "@/components/home/CtaBand";
import { TerminDialog } from "@/components/TerminDialog";

export default function ServiceDetail() {
  const { slug } = useParams();
  const idx = services.findIndex((s) => s.slug === slug);
  if (idx === -1) return <Navigate to="/leistungen" replace />;
  const s = services[idx];
  const nextS = services[(idx + 1) % services.length];
  const schema = { "@context": "https://schema.org", "@type": "Service", name: s.title, description: s.short, provider: { "@type": "AutoRepair", name: company.name }, areaServed: company.city };

  return (
    <>
      <Seo title={`${s.title} – NIVO GARAGE`} description={s.short} schema={schema} />
      <section className="pt-32 md:pt-44 pb-12">
        <div className="container-x">
          <FadeUp>
            <Link to="/leistungen" data-testid="service-back-link" className="inline-flex items-center gap-2 text-[14px] text-[color:var(--ink-2)] hover:text-[color:var(--ink)] mb-8"><ArrowLeft className="h-4 w-4" /> Alle Leistungen</Link>
          </FadeUp>
          <p className="eyebrow mb-4">Leistung</p>
          <Lines as="h1" lines={[s.title]} className="h-display" data-testid="service-detail-title" />
          <FadeUp delay={0.2} className="lead mt-6 max-w-2xl">{s.short}</FadeUp>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <FadeUp className="text-[color:var(--ink-2)] text-[18px] leading-relaxed">{s.intro}</FadeUp>
            <FadeUp delay={0.1} className="mt-10">
              <p className="eyebrow mb-4">Das ist enthalten</p>
              <ul className="border-t hairline">
                {s.includes.map((it, i) => (
                  <li key={it} className="flex gap-5 py-4 border-b hairline" data-testid={`service-include-${i}`}>
                    <span className="text-[13px] text-[color:var(--violet)] mono pt-1">0{i + 1}</span>
                    <span className="text-[17px]">{it}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.15} className="mt-10">
              <TerminDialog testId="service-detail-cta" className="btn-violet" />
            </FadeUp>
          </div>
          <FadeUp delay={0.1} className="lg:col-span-5">
            <div className="panel p-7">
              <p className="eyebrow mb-4">So arbeiten wir</p>
              <ul className="space-y-4">
                {fairness.map((f) => (
                  <li key={f.t}>
                    <p className="font-medium">{f.t}</p>
                    <p className="text-[15px] text-[color:var(--ink-2)]">{f.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      <div className="container-x pb-16">
        <Link to={`/leistungen/${nextS.slug}`} data-testid="service-next-link" className="group flex items-center justify-between border-t hairline pt-6">
          <div>
            <p className="eyebrow mb-1">Nächste Leistung</p>
            <p className="text-[24px] md:text-[32px] font-semibold tracking-[-0.02em] group-hover:text-[color:var(--violet)] transition-colors">{nextS.title}</p>
          </div>
          <ArrowRight className="h-6 w-6 text-[color:var(--ink-3)] group-hover:text-[color:var(--violet)] group-hover:translate-x-1 transition-[color,transform]" />
        </Link>
      </div>
      <CtaBand />
    </>
  );
}
